import React, {useEffect, useState} from "react";
import VerticalBoxConnector from "./VerticalBoxConnector";
import {HostBubblePlaceHolder} from "./HostBubble";
import LineConnector from "./LineConnector";
import {createUseStyles, useTheme} from "react-jss";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {Theme} from "engage-client/theme";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";

export const BoxDimension = 134;
const TitleHeight = 24;
const TripleLineHeight = 80;

const useStyles = createUseStyles<Theme>((theme) => ({
	virtualHost: {
		display: "flex",
	},
	hostContainer: {
		display: "flex",
		flexDirection: "column",
		width: BoxDimension,
		[theme.breakpoints.up("xxl")] : {
			width: props => props.xxlBoxDimension
		}
	},
	hostText: {
		padding: 1,
		textAlign: "center",
		backgroundColor: theme.presenterVideo.textBackground,
		height: TitleHeight,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		[theme.breakpoints.up("xxl")] : {
			height: props => props.xxlTitleHeight,
			alignContent: "center"
		}
	},
	hostBox: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: BoxDimension,
		width: BoxDimension,
		backgroundColor: "rgba(3,3,3,0.3)",
		border: "1px solid #707070",
		[theme.breakpoints.up("xxl")] : {
			height: props => props.xxlBoxDimension,
			width: props => props.xxlBoxDimension
		}
	},
	verticalBoxReplacement: {
		width: props => props.connectorWidth
	},
	threeLineContainer: {
		paddingTop: BoxDimension / 2 + TitleHeight - TripleLineHeight / 2,
		[theme.breakpoints.up("xxl")] : {
			paddingTop: props => props.xxlBoxDimension / 2 + props.xxlTitleHeight - TripleLineHeight / 2,
		}
	}
}));

type VirtualHostProps = {
	embedURL: string
	connectorWidth: number
	showThreeLine:boolean
	hostName?:string
}

export default function VirtualHost({embedURL,connectorWidth,showThreeLine, hostName}:VirtualHostProps) {
	const windowHeight = useWindowSize().height;
	let xxlBoxDimension = -1;
	let xxlTitleHeight = -1;
	if (windowHeight) {
		xxlBoxDimension = (12 * windowHeight)/100;
		xxlTitleHeight = (3 * windowHeight) / 100;
	}
	const classes = useStyles({
		connectorWidth,
		xxlBoxDimension,
		xxlTitleHeight
	});
	const theme:Theme = useTheme();
	const isXxl = useMediaQuery(theme.breakpoints.up("xxl"));
	const displayedHostName = hostName?.length ? hostName : "Host";
	return (
		<div className={classes.virtualHost}>
			{theme.presenterVideo.simpleDesign ? <div className={classes.verticalBoxReplacement}/> : <VerticalBoxConnector width={connectorWidth} connectionPointY={(isXxl ? xxlTitleHeight : TitleHeight) + (isXxl ? xxlBoxDimension : BoxDimension) / 2}/>}
			<div className={classes.hostContainer}>
				<DisplayText variant={TextVariant.s20med} className={classes.hostText}>{displayedHostName}</DisplayText>
				<div className={classes.hostBox}>
					<HostBubblePlaceHolder embedURL={embedURL} width={isXxl ? xxlBoxDimension - 20 : 114}/>
				</div>
			</div>
			{ theme.presenterVideo.simpleDesign ? <div style={{width: 14}}/>
				: showThreeLine ?
				<div className={classes.threeLineContainer}>
					<LineConnector numberOfLines={3} thickness={2} color="white" opacity={0.47}
								   width={14} height={TripleLineHeight}/>
				</div> : undefined
			}
		</div>
	);
}

const useMobileStyles = createUseStyles({
	mobileHost: {
		position: "absolute",
		top: 0,
		right: 0,
		//Give it some room above the Get Ready... label
		bottom: 10,
		left: 0,
		display: "flex",
		justifyContent: "center",
		alignItems: props => props.bottom ? "flex-end" : "center",
		transform: ({visible}) => visible ? "translateX(0)" : "translateX(calc(-50% - 100px))",
	}
});

type MobileVirtualHostProps = {
	embedURL: string
	visible: boolean
	bottom?: boolean
}

export function MobileVirtualHost({embedURL, visible, bottom = false}: MobileVirtualHostProps) {
	const classes = useMobileStyles({visible, bottom});

	// Location dep is passed into the host bubble as a mechanism for letting the host bubble know when to update
	// it's position.
	const [locationDep, setLocationDep] = useState(false);

	useEffect(() => {
		setLocationDep(locDep => !locDep);
	}, [visible]);

	return <div className={classes.mobileHost}>
		<HostBubblePlaceHolder embedURL={embedURL} dep={locationDep} />
	</div>;
}
